import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import DynamicFormSystem from "./components/DynamicFormSystem";
import CampaignList from "./components/CampaignList";
import TeamList from "./components/TeamList";
import CustomerList from "./components/CustomerList";
import InventoryForm from "./components/InventoryForm";
import InventoryDashboardForm from "./components/InventoryDashboardForm";
import OrderListForm from "./components/OrderListForm";
import OrderForm from "./components/OrderForm";
import OrderPreviewPublic from "./components/OrderPreviewPublic";
import WholesaleOrderList from "./components/WholesaleOrderList";
import WholesaleOrderForm from "./components/WholesaleOrderForm";
import SaleReportForm from "./components/SaleReportForm";
import SaleReportDashboardForm from "./components/SaleReportDashboardForm";
import ShipmentReportList from "./components/ShipmentReportList";
import ShipmentReportDetail from "./components/ShipmentReportDetail";
import ShipmentReportForm from "./components/ShipmentReportForm";
import LoginPage from "./components/LoginPage";
import RegisterPage from "./components/RegisterPage";
import UpdateProfilePage from "./components/UpdateProfilePage";
import MissingInfoModal from "./components/MissingInfoModal";
import StandaloneOrgChart from "./components/StandaloneOrgChart";
import SystemHeader from "./components/SystemHeader";
import Dashboard from "./components/Dashboard";
import ReelsList from "./components/reels/reels-list";
import ReelsForm from "./components/reels/reels-form";
import ReelTrendingList from "./components/reel-trending/reel-trending-list";
import ReelTrendingForm from "./components/reel-trending/reel-trending-form";
import LivestreamList from "./components/livestream/livestream-list";
import LivestreamForm from "./components/livestream/livestream-form";
import TeamMeetingList from "./components/team-meeting/team-meeting-list";
import TeamMeetingForm from "./components/team-meeting/team-meeting-form";
import MarketList from "./components/market/market-list";
import MarketForm from "./components/market/market-form";
import SpaMarketList from "./components/spa-market/spa-market-list";
import SpaMarketForm from "./components/spa-market/spa-market-form";
import DailyReportList from "./components/daily-report/daily-report-list";
import DailyReportForm from "./components/daily-report/daily-report-form";
import ToggleSalesList from "./components/toggle-sales/toggle-sales-list";
import ToggleSalesForm from "./components/toggle-sales/toggle-sales-form";
import FeatureAdminPage from "./components/admin/FeatureAdminPage";
import KienThucList from "./components/kienthuc/KienThucList";
import KienThucDetail from "./components/kienthuc/KienThucDetail";
import KienThucSanPhamPage from "./components/kienthuc/KienThucSanPhamPage";
import ScorePage from "./components/score/score-page";
import F1List from "./components/f1-management/f1-list";
import QuanSoPage from "./components/quan-so/quan-so-page";
import ReelsAutoCheckList from "./components/reels-auto-check/reels-auto-check-list";
import KhoeThanhTichPage from "./components/khoe-thanh-tich/khoe-thanh-tich-page";
import KhoeThanhTichThangPage from "./components/khoe-thanh-tich-thang/khoe-thanh-tich-thang-page";
import ThuVienThongDiepPage from "./components/thu-vien-thong-diep/thu-vien-thong-diep-page";
import KhoAnhPage from "./components/kho-anh/kho-anh-page";
import KhenThuongPage from "./components/khen-thuong/khen-thuong-page";
import KhenThuongChiTietPage from "./components/khen-thuong/khen-thuong-chi-tiet-page";
import TheChucMungPage from "./components/khen-thuong/the-chuc-mung";
import DuLich2026Form from "./components/khen-thuong/du-lich-2026-form";
import DuLich2026TrangThai from "./components/khen-thuong/du-lich-2026-trang-thai";
import KhachLePage from "./components/khach-le/khach-le-page";
import VideoPage from "./components/video/video-page";
import KienThucKinhDoanhPage, { ComboChiTietPage } from "./components/kienthuc/KienThucKinhDoanhPage";
import { FeedbackDanhSachPage, FeedbackChiTietPage, KhoFeedbackPage } from "./components/feedback/feedback-page";
import DangTrienKhaiPage from "./components/DangTrienKhaiPage";
import AutoLoginPage from "./components/AutoLoginPage";
import VersionBadge from "./components/VersionBadge";
import AuthManager from "./utils/AuthManager";
import API from "./services/API";
import { ScoresAPI } from "./services/feature-api";
import { getPageConfig } from "./config/pageStatusConfig";
import { FeatureConfigProvider, useFeatureConfig } from "./contexts/FeatureConfigContext";

// AuthWrapper component để kiểm tra authentication - SIMPLIFIED VERSION
const AuthWrapper = ({ children, onLogout }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userInfo, setUserInfo] = useState(null);
    const [totalPoints, setTotalPoints] = useState(0);
    const [accessToken, setAccessToken] = useState(null);
    
    // Use feature config hook
    const { loadFeatures, features } = useFeatureConfig();

    const fetchUserPoints = useCallback(async (accessToken) => {
        try {
            console.log("🚀 Fetching user points...");
            const now = new Date();
            const month = now.getMonth() + 1;
            const year = now.getFullYear();
            const result = await ScoresAPI.getScores(accessToken, month, year);

            if (result.success && result.data) {
                const total = result.data.totalScore || 0;
                setTotalPoints(total);
                console.log("✅ User points loaded:", total);
            } else {
                setTotalPoints(0);
            }
        } catch (error) {
            console.error("❌ Error fetching user points:", error);
            setTotalPoints(0);
        }
    }, []);

    const fetchUserProfile = useCallback(async (accessToken) => {
        try {
            console.log("🚀 Fetching user profile...");
            const profileResult = await API.getUserProfile(accessToken);

            if (profileResult.success && profileResult.user) {
                const userData = profileResult.user.userData;
                const updatedUserInfo = {
                    id: userData.id || userData.userId,
                    name: userData.name,
                    phoneNumber: userData.phone,
                    email: userData.email,
                    avatar: userData.avatar,
                    // Only override code/facebook if Yody API actually returns them
                    // (CungVeDich login already sets these — don't overwrite with null)
                    ...(userData.code && { code: userData.code }),
                    ...(userData.facebook && { facebook: userData.facebook }),
                };
                
                setUserInfo((prev) => ({ ...prev, ...updatedUserInfo }));
                AuthManager.updateUserInfo(updatedUserInfo);
                console.log("✅ User profile loaded successfully");
            }
        } catch (error) {
            console.error("❌ Error fetching user profile:", error);
        }
    }, []);

    const checkAuth = useCallback(async () => {
        console.log("🔍 Checking authentication...");

        try {
            // Check if user has valid session in localStorage
            const session = AuthManager.getSession();
            
            if (!session || !session.userInfo) {
                console.log("❌ No valid session found");
                setIsAuthenticated(false);
                setIsLoading(false);
                return;
            }

            // Check session expiry
            if (AuthManager.isSessionExpired()) {
                console.log("⚠️ Session expired, clearing and redirecting to login");
                AuthManager.clearSession();
                setIsAuthenticated(false);
                setIsLoading(false);
                return;
            }

            // Session valid - refresh timestamp to extend 30-day window
            AuthManager.refreshSession();

            // Restore user state
            console.log("✅ Valid session found, restoring user state...");
            const { userInfo: savedUserInfo, accessToken: savedAccessToken } = session;

            setUserInfo(savedUserInfo);
            setAccessToken(savedAccessToken);
            setIsAuthenticated(true);
            // UNBLOCK render dashboard NGAY khi session hợp lệ — KHÔNG đợi 3 fetch dưới.
            // Trước đây await Promise.all → màn trắng 3-5s sau login. Giờ dashboard show ngay,
            // điểm + profile + features load nền (placeholder "Đang tải..." cho slot chưa có data).
            setIsLoading(false);

            if (savedAccessToken) {
                Promise.allSettled([
                    fetchUserPoints(savedAccessToken),
                    fetchUserProfile(savedAccessToken),
                    loadFeatures(savedAccessToken),
                ]).catch(error => console.error("⚠️ Background fetch failed:", error));
            }
        } catch (error) {
            console.error("❌ Error checking authentication:", error);
            setIsAuthenticated(false);
            setIsLoading(false);
        }
    }, [fetchUserPoints, fetchUserProfile, loadFeatures]);

    // Check authentication on mount
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Listen 'auth:expired' từ feature-api.js handleResponse khi BE trả 401
    // → tự logout + redirect /login (thay vì để banner đỏ 'Unauthorized' hiện trên UI)
    useEffect(() => {
        const onAuthExpired = () => {
            // Đang impersonate mà token 15' hết hạn → quay về tài khoản gốc (các cấp trên trong ngăn xếp cũng hết hạn), KHÔNG xoá phiên gốc.
            if (AuthManager.isImpersonating()) {
                AuthManager.clearImpersonation();
                try { window.alert("Hết phiên xem tài khoản F1. Quay về tài khoản gốc."); } catch {}
                window.location.replace("/f1");
                return;
            }
            console.log("🔒 auth:expired event → logout + redirect /login");
            try { window.alert("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại."); } catch {}
            AuthManager.clearSession();
            setIsAuthenticated(false);
            setUserInfo(null);
            setTotalPoints(0);
            setAccessToken(null);
            const currentPath = window.location.pathname;
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
        };
        window.addEventListener("auth:expired", onAuthExpired);
        return () => window.removeEventListener("auth:expired", onAuthExpired);
    }, []);

    // Manual refresh points function
    const handleRefreshPoints = async () => {
        if (accessToken) {
            console.log("🔄 Refreshing user data...");
            await Promise.all([fetchUserPoints(accessToken), fetchUserProfile(accessToken)]);
        } else {
            console.warn("⚠️ Cannot refresh: no access token available");
        }
    };

    const handleLogout = () => {
        // Đang impersonate → "Đăng xuất" = thoát xem, quay về tài khoản gốc (KHÔNG xoá phiên gốc).
        if (AuthManager.isImpersonating()) {
            AuthManager.clearImpersonation();
            window.location.replace("/f1");
            return;
        }
        console.log("🚪 Logging out...");
        // Clear all auth data
        AuthManager.clearSession();
        setIsAuthenticated(false);
        setUserInfo(null);
        setTotalPoints(0);
        setAccessToken(null);
        // Redirect to login
        window.location.href = "/login";
    };

    // Thoát "xem dưới tài khoản F1": lùi 1 cấp (ngăn xếp sessionStorage) — hết cấp thì về tài khoản gốc; nút riêng về thẳng gốc.
    const handleExitImpersonation = (veGoc = false) => {
        if (veGoc) AuthManager.clearImpersonation(); else AuthManager.stopImpersonation();
        window.location.replace("/f1");
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Đang kiểm tra xác thực...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // Lưu path hiện tại để redirect về sau khi đăng nhập
        const currentPath = window.location.pathname;
        const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
        return <Navigate to={loginUrl} replace />;
    }

    // Tính năng màn hình chính bị admin khoá (feature_configs BLOCK, trang admin Cấu hình tính năng) → vào bằng đường nào cũng gặp thông báo
    const duongDan = window.location.pathname;
    const tinhNangKhoa = features.find((f) => f.stt != null && f.isAccess === false && f.path && (duongDan === f.path || duongDan.startsWith(f.path + '/')));
    if (tinhNangKhoa) {
        return (
            <TemporaryClosedPage notificationText={tinhNangKhoa.notificationText || 'Tính năng đang tạm khoá'}
                gradientColors="from-orange-500 to-red-600" bgColors="from-orange-50 to-red-100"
                userInfo={userInfo} onLogout={handleLogout} totalPoints={totalPoints} accessToken={accessToken} onRefreshPoints={handleRefreshPoints} />
        );
    }

    // Clone children và truyền props cần thiết
    const childEl = React.cloneElement(children, {
        userInfo,
        onLogout: handleLogout,
        totalPoints: totalPoints,
        accessToken: accessToken,
        onRefreshPoints: handleRefreshPoints,
    });

    // Banner "Đang xem dưới tài khoản F1" + nút quay về — chỉ hiện khi đang impersonate (sessionStorage).
    // Popup bổ sung thông tin thiếu: chỉ ở phiên thật (không bật khi đang xem dưới TK F1).
    if (!AuthManager.isImpersonating()) {
        return (
            <>
                {childEl}
                <MissingInfoModal accessToken={accessToken} />
            </>
        );
    }
    // Xem nhiều cấp (gốc → F1 → F2…): banner hiện đường đi + nút lùi 1 cấp về đúng người trước; nút "Gốc" về thẳng tài khoản gốc
    const capTren = AuthManager.getImpersonationStack();
    const tenNgan = (u) => [u?.code, u?.facebook || u?.name].filter(Boolean).join(" - ");
    const truoc = capTren[capTren.length - 1]?.userInfo;
    return (
        <>
            <div className="bg-amber-500 text-white px-2 py-1.5 flex items-center gap-1.5 text-[12px] shadow-md">
                <span className="material-symbols-rounded shrink-0" style={{ fontSize: "16px" }}>visibility</span>
                <span className="flex-1 min-w-0 truncate">
                    {capTren.length > 0 && <span className="text-amber-100">{capTren.map((c) => tenNgan(c.userInfo)).join(" › ")} › </span>}
                    <b>{tenNgan(userInfo)}</b>
                </span>
                <button onClick={() => handleExitImpersonation(false)}
                        className="flex-shrink-0 px-2.5 py-1 rounded-md bg-white text-amber-700 font-bold text-[11px] hover:bg-amber-50 max-w-[46%] truncate">
                    ← {truoc ? `Về ${tenNgan(truoc)}` : "Quay về tài khoản gốc"}
                </button>
                {capTren.length > 0 && (
                    <button onClick={() => handleExitImpersonation(true)} title="Về thẳng tài khoản gốc"
                            className="flex-shrink-0 px-2 py-1 rounded-md bg-amber-700/60 text-white font-bold text-[11px]">Gốc</button>
                )}
            </div>
            {childEl}
        </>
    );
};

// LoginWrapper component để xử lý login/register
const LoginWrapper = () => {
    const [currentPage, setCurrentPage] = useState("login");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [mainPageUrl, setMainPageUrl] = useState("");
    const [resultPageUrl, setResultPageUrl] = useState("");

    const [campaignInfo, setCampaignInfo] = useState(null);
    const [campaignLoading, setCampaignLoading] = useState(false);

    // Lấy redirect path từ URL params
    const getRedirectPath = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirect = urlParams.get("redirect");
        return redirect || "/dashboard"; // Default to dashboard
    };

    // Nếu user đã đăng nhập, chuyển hướng về trang chính
    useEffect(() => {
        const session = AuthManager.getSession();
        if (session && !AuthManager.isSessionExpired()) {
            const redirectPath = getRedirectPath();
            window.location.href = redirectPath;
        }
    }, []);

    // Extract campaign code từ redirect path
    const extractCampaignCodeFromRedirect = useCallback(() => {
        const redirectPath = getRedirectPath();
        if (redirectPath.startsWith("/chien-dich/")) {
            return redirectPath.split("/")[2];
        }
        return null;
    }, []);

    const fetchCampaignInfo = useCallback(async (campaignCode) => {
        if (!campaignCode) return;

        setCampaignLoading(true);

        try {
            console.log("🚀 LoginWrapper - Fetching campaign info for code:", campaignCode);
            const result = await API.getCampaignInfo(campaignCode);

            if (result.success) {
                setCampaignInfo(result.campaign);
                console.log("✅ LoginWrapper - Campaign info loaded:", result.campaign);

                // Update URLs from campaign
                if (result.mainUrl) {
                    console.log("🔄 LoginWrapper - Setting main URL from campaign:", result.mainUrl);
                    setMainPageUrl(result.mainUrl);
                }

                if (result.successUrl) {
                    console.log("🔄 LoginWrapper - Setting success URL from campaign:", result.successUrl);
                    setResultPageUrl(result.successUrl);
                }
            }
        } catch (error) {
            console.error("❌ LoginWrapper - Failed to fetch campaign info:", error);
            // Don't block login if campaign fetch fails
        } finally {
            setCampaignLoading(false);
        }
    }, []);

    // Fetch campaign info nếu redirect path chứa campaign code
    useEffect(() => {
        const campaignCode = extractCampaignCodeFromRedirect();
        if (campaignCode) {
            fetchCampaignInfo(campaignCode);
        }
    }, [extractCampaignCodeFromRedirect, fetchCampaignInfo]);

    const handleLoginSuccess = async (userInfo, accessToken) => {
        // Lấy path cần redirect về
        const redirectPath = getRedirectPath();

        console.log("🔍 Login success, redirecting to:", redirectPath);

        // Save session using AuthManager
        AuthManager.saveSession(userInfo, accessToken);

        // Redirect without token in URL
        window.location.href = redirectPath;
    };

    const handleRegisterSuccess = async (userInfo, accessToken) => {
        // Lấy path cần redirect về
        const redirectPath = getRedirectPath();

        console.log("🔍 Register success, redirecting to:", redirectPath);

        // Save session using AuthManager
        AuthManager.saveSession(userInfo, accessToken);

        // Redirect without token in URL
        window.location.href = redirectPath;
    };

    const handleGoToRegister = () => {
        setCurrentPage("register");
    };

    const handleBackToLogin = () => {
        setCurrentPage("login");
    };

    // Show loading screen during campaign loading
    if (campaignLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Đang tải thông tin chiến dịch...</p>
                </div>
            </div>
        );
    }

    const renderCurrentPage = () => {
        switch (currentPage) {
            case "login":
                return (
                    <LoginPage
                        onLogin={handleLoginSuccess}
                        onRegister={handleGoToRegister}
                        setMainPageUrl={setMainPageUrl}
                        setResultPageUrl={setResultPageUrl}
                        setPhoneNumber={setPhoneNumber}
                        setError={setError}
                        error={error}
                        loading={loading}
                        setLoading={setLoading}
                        setUserInfo={() => {}}
                        mainPageUrl={mainPageUrl}
                        resultPageUrl={resultPageUrl}
                        campaignInfo={campaignInfo}
                    />
                );
            case "register":
                return (
                    <RegisterPage
                        mode="register"
                        phoneNumber={phoneNumber}
                        onBackToLogin={handleBackToLogin}
                        setError={setError}
                        error={error}
                        loading={loading}
                        setLoading={setLoading}
                    />
                );
            default:
                return (
                    <LoginPage
                        onLogin={handleLoginSuccess}
                        onRegister={handleGoToRegister}
                        setMainPageUrl={setMainPageUrl}
                        setResultPageUrl={setResultPageUrl}
                        setPhoneNumber={setPhoneNumber}
                        setError={setError}
                        error={error}
                        loading={loading}
                        setLoading={setLoading}
                        campaignInfo={campaignInfo}
                    />
                );
        }
    };

    return <div className="animate-fade-in">{renderCurrentPage()}</div>;
};

// Component hiển thị khi trang tạm đóng
const TemporaryClosedPage = ({ 
    notificationText = "cập nhật", 
    gradientColors = "from-purple-500 to-indigo-600", 
    bgColors = "from-purple-50 to-indigo-100",
    userInfo,
    onLogout,
    totalPoints,
    accessToken,
    onRefreshPoints
}) => {
    return (
        <div className="min-h-screen bg-gray-50">
            <SystemHeader 
                userInfo={userInfo}
                onLogout={onLogout}
                totalPoints={totalPoints}
                accessToken={accessToken}
                onRefreshPoints={onRefreshPoints}
            />
            
            <div className={`min-h-[calc(100vh-80px)] bg-gradient-to-br ${bgColors} flex items-center justify-center p-4`}>
                <div className="max-w-2xl w-full">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
                        <div className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br ${gradientColors} rounded-full flex items-center justify-center`}>
                            <svg
                                className="w-12 h-12 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                                />
                            </svg>
                        </div>
                        <h1 className={`text-3xl md:text-4xl font-bold text-center mb-4 bg-gradient-to-r ${gradientColors} bg-clip-text text-transparent`}>
                            Thông báo
                        </h1>
                        <div className="text-center space-y-4">
                            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                                {notificationText}
                            </p>
                        </div>
                        <div className="mt-8 flex justify-center">
                            <svg
                                className={`w-8 h-8 text-gradient-to-r ${gradientColors} animate-bounce`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Wrapper cho các trang cần kiểm tra trạng thái enable
const ConditionalPageWrapper = ({ children, path, ...props }) => {
    const { getFeatureByPath, isLoading, features } = useFeatureConfig();
    
    // Show loading spinner while features are being loaded
    if (isLoading && features.length === 0) {
        console.log(`⏳ ConditionalPageWrapper: Loading features for path "${path}"`);
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Đang kiểm tra quyền truy cập...</p>
                </div>
            </div>
        );
    }
    
    // Try to get feature from feature config
    const feature = getFeatureByPath(path);
    
    // If feature config available, use it
    if (feature) {
        // Check if user has access
        if (!feature.isAccess) {
            console.log(`🔒 ConditionalPageWrapper: Access denied for path "${path}"`, feature);
            return (
                <TemporaryClosedPage 
                    notificationText={feature.notificationText || "cập nhật"}
                    gradientColors="from-orange-500 to-red-600"
                    bgColors="from-orange-50 to-red-100"
                    {...props} 
                />
            );
        }
        
        console.log(`✅ ConditionalPageWrapper: Access granted for path "${path}"`);
        // Clone children và truyền props từ AuthWrapper
        return React.cloneElement(children, props);
    }
    
    // Fallback to pageStatusConfig if feature not found (backward compatibility)
    console.warn(`⚠️ ConditionalPageWrapper: No feature config found for "${path}", using pageStatusConfig`);
    const config = getPageConfig(path);
    
    // Nếu không có config hoặc enabled = false, hiển thị thông báo
    if (!config || !config.enabled) {
        return (
            <TemporaryClosedPage 
                notificationText={config?.notificationText || "cập nhật"}
                gradientColors={config?.gradientColors || "from-purple-500 to-indigo-600"}
                bgColors={config?.bgColors || "from-purple-50 to-indigo-100"}
                {...props} 
            />
        );
    }
    
    // Clone children và truyền props từ AuthWrapper
    return React.cloneElement(children, props);
};

function App() {
    return (
        <FeatureConfigProvider>
            <VersionBadge />
            <Router>
                <div className="App">
                    <Routes>
                        {/* Route mặc định - chuyển hướng đến /dashboard */}
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />

                        {/* Trang đăng nhập/đăng ký */}
                        <Route path="/login" element={<LoginWrapper />} />
                        <Route path="/register" element={<LoginWrapper />} />
                        <Route path="/auto-login" element={<AutoLoginPage />} />

                        {/* Trang Dashboard - yêu cầu đăng nhập */}
                        <Route
                            path="/dashboard"
                            element={
                                <AuthWrapper>
                                    <Dashboard />
                                </AuthWrapper>
                            }
                        />

                        {/* Trang Cập nhật thông tin (mục "0" trong menu) - yêu cầu đăng nhập */}
                        <Route
                            path="/cap-nhat-thong-tin"
                            element={
                                <AuthWrapper>
                                    <UpdateProfilePage />
                                </AuthWrapper>
                            }
                        />

                        {/* Trang danh sách chiến dịch - yêu cầu đăng nhập */}
                        <Route
                            path="/chien-dich"
                            element={
                                <AuthWrapper>
                                    <CampaignList />
                                </AuthWrapper>
                            }
                        />

                        {/* Route cho từng chiến dịch cụ thể - yêu cầu đăng nhập */}
                        <Route
                            path="/chien-dich/:campaignCode"
                            element={
                                <AuthWrapper>
                                    <DynamicFormSystem />
                                </AuthWrapper>
                            }
                        />

                        {/* Trang tổ chức - yêu cầu đăng nhập */}
                        <Route
                            path="/to-chuc"
                            element={
                                <AuthWrapper>
                                    <TeamList />
                                </AuthWrapper>
                            }
                        />

                        {/* Trang danh sách khách hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/khach-hang"
                            element={
                                <AuthWrapper>
                                    <CustomerList />
                                </AuthWrapper>
                            }
                        />

                        {/* Trang danh sách đơn hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/don-hang"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/don-hang">
                                        <OrderListForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang tạo đơn hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/don-hang/tao-moi"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/don-hang/tao-moi">
                                        <OrderForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang chỉnh sửa đơn hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/don-hang/chinh-sua"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/don-hang/chinh-sua">
                                        <OrderForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang danh sách đơn hàng bán buôn - yêu cầu đăng nhập */}
                        <Route
                            path="/don-hang-ban-buon"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/don-hang-ban-buon">
                                        <WholesaleOrderList />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang tạo đơn hàng bán buôn - yêu cầu đăng nhập */}
                        <Route
                            path="/don-hang-ban-buon/tao-moi"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/don-hang-ban-buon/tao-moi">
                                        <WholesaleOrderForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang chỉnh sửa đơn hàng bán buôn - yêu cầu đăng nhập */}
                        <Route
                            path="/don-hang-ban-buon/chinh-sua"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/don-hang-ban-buon/chinh-sua">
                                        <WholesaleOrderForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang kho hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/kho-hang"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/kho-hang">
                                        <InventoryDashboardForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />
                        <Route
                            path="/kho-hang/chinh-sua"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/kho-hang/chinh-sua">
                                        <InventoryForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang báo cáo doanh số - yêu cầu đăng nhập */}
                        <Route
                            path="/doanh-so"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/doanh-so">
                                        <SaleReportDashboardForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang chỉnh sửa doanh số - yêu cầu đăng nhập */}
                        <Route
                            path="/doanh-so/chinh-sua"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/doanh-so/chinh-sua">
                                        <SaleReportForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang danh sách trả hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/tra-hang"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/tra-hang">
                                        <ShipmentReportList />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang chi tiết trả hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/tra-hang/chi-tiet"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/tra-hang/chi-tiet">
                                        <ShipmentReportDetail />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang tạo mới trả hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/tra-hang/tao-moi"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/tra-hang/tao-moi">
                                        <ShipmentReportForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Trang chỉnh sửa trả hàng - yêu cầu đăng nhập */}
                        <Route
                            path="/tra-hang/chinh-sua"
                            element={
                                <AuthWrapper>
                                    <ConditionalPageWrapper path="/tra-hang/chinh-sua">
                                        <ShipmentReportForm />
                                    </ConditionalPageWrapper>
                                </AuthWrapper>
                            }
                        />

                        {/* Backward compatibility - Redirect old shipment-report paths */}
                        <Route path="/shipment-reports" element={<Navigate to="/tra-hang" replace />} />
                        <Route path="/shipment-report-detail" element={<Navigate to="/tra-hang/chi-tiet" replace />} />
                        <Route path="/shipment-report-form" element={<Navigate to="/tra-hang/tao-moi" replace />} />

                        {/* Trang xem preview đơn hàng công khai - không yêu cầu đăng nhập */}
                        <Route path="/don-hang-preview/:id" element={<OrderPreviewPublic />} />

                        {/* Trang xem sơ đồ tổ chức theo username - không yêu cầu đăng nhập */}
                        <Route path="/to-chuc-chart/:username" element={<StandaloneOrgChart />} />

                        {/* Reels */}
                        <Route path="/reels" element={<AuthWrapper><ReelsList /></AuthWrapper>} />
                        <Route path="/reels/tao-moi" element={<AuthWrapper><ReelsForm /></AuthWrapper>} />
                        <Route path="/reels/chinh-sua" element={<AuthWrapper><ReelsForm /></AuthWrapper>} />

                        {/* Reel xu huong */}
                        <Route path="/reel-xu-huong" element={<AuthWrapper><ReelTrendingList /></AuthWrapper>} />
                        <Route path="/reel-xu-huong/tao-moi" element={<AuthWrapper><ReelTrendingForm /></AuthWrapper>} />
                        <Route path="/reel-xu-huong/chinh-sua" element={<AuthWrapper><ReelTrendingForm /></AuthWrapper>} />

                        {/* Livestream */}
                        <Route path="/livestream" element={<AuthWrapper><LivestreamList /></AuthWrapper>} />
                        <Route path="/livestream/tao-moi" element={<AuthWrapper><LivestreamForm /></AuthWrapper>} />
                        <Route path="/livestream/chinh-sua" element={<AuthWrapper><LivestreamForm /></AuthWrapper>} />

                        {/* Hop Team */}
                        <Route path="/hop-team" element={<AuthWrapper><TeamMeetingList /></AuthWrapper>} />
                        <Route path="/hop-team/tao-moi" element={<AuthWrapper><TeamMeetingForm /></AuthWrapper>} />
                        <Route path="/hop-team/chinh-sua" element={<AuthWrapper><TeamMeetingForm /></AuthWrapper>} />

                        {/* Thi truong thuong */}
                        <Route path="/thi-truong" element={<AuthWrapper><MarketList /></AuthWrapper>} />
                        <Route path="/thi-truong/tao-moi" element={<AuthWrapper><MarketForm /></AuthWrapper>} />
                        <Route path="/thi-truong/chinh-sua" element={<AuthWrapper><MarketForm /></AuthWrapper>} />

                        {/* Thi truong Spa */}
                        <Route path="/thi-truong-spa" element={<AuthWrapper><SpaMarketList /></AuthWrapper>} />
                        <Route path="/thi-truong-spa/tao-moi" element={<AuthWrapper><SpaMarketForm /></AuthWrapper>} />
                        <Route path="/thi-truong-spa/chinh-sua" element={<AuthWrapper><SpaMarketForm /></AuthWrapper>} />

                        {/* Bao cao ngay */}
                        <Route path="/bao-cao-ngay" element={<AuthWrapper><DailyReportList /></AuthWrapper>} />
                        <Route path="/bao-cao-ngay/tao-moi" element={<AuthWrapper><DailyReportForm /></AuthWrapper>} />
                        <Route path="/bao-cao-ngay/chinh-sua" element={<AuthWrapper><DailyReportForm /></AuthWrapper>} />

                        {/* Bat doanh so */}
                        <Route path="/bat-doanh-so" element={<AuthWrapper><ToggleSalesList /></AuthWrapper>} />
                        <Route path="/bat-doanh-so/tao-moi" element={<AuthWrapper><ToggleSalesForm /></AuthWrapper>} />
                        <Route path="/bat-doanh-so/chinh-sua" element={<AuthWrapper><ToggleSalesForm /></AuthWrapper>} />

                        {/* Admin - Feature management */}
                        <Route path="/admin/features" element={<AuthWrapper><FeatureAdminPage /></AuthWrapper>} />

                        {/* Kiến thức */}
                        <Route path="/kien-thuc" element={<AuthWrapper><KienThucList /></AuthWrapper>} />
                        <Route path="/kien-thuc/:slug" element={<AuthWrapper><KienThucDetail /></AuthWrapper>} />

                        {/* Điểm số */}
                        <Route path="/diem-so" element={<AuthWrapper><ScorePage /></AuthWrapper>} />

                        {/* F1 Management */}
                        <Route path="/f1" element={<AuthWrapper><F1List /></AuthWrapper>} />

                        {/* QT Quân Số (STT 3.1) — quân số toàn nhánh, hardcoded, no feature gate */}
                        <Route path="/quan-so" element={<AuthWrapper><QuanSoPage /></AuthWrapper>} />

                        {/* QL Khách lẻ (STT 8) — hardcoded, no feature gate */}
                        <Route path="/khach-le" element={<AuthWrapper><KhachLePage /></AuthWrapper>} />

                        {/* Video đào tạo trên Mux — link gửi đại lý, bắt đăng nhập; admin xem ai đã xem ở /video-dao-tao (video/video-page.js) */}
                        <Route path="/video/:ma" element={<AuthWrapper><VideoPage /></AuthWrapper>} />

                        {/* Đang triển khai: 15, 25 (16 có combo, 17 có mật khẩu, 24 có trang) */}
                        <Route path="/quy-dinh-cong-ty" element={<AuthWrapper><DangTrienKhaiPage title="Quy định công ty" icon="gavel" /></AuthWrapper>} />
                        {/* 16 Kiến thức kinh doanh — khu Combo sản phẩm (kienthuc/KienThucKinhDoanhPage.js, admin /combo-san-pham) */}
                        <Route path="/kien-thuc-kinh-doanh" element={<AuthWrapper><KienThucKinhDoanhPage /></AuthWrapper>} />
                        <Route path="/kien-thuc-kinh-doanh/combo/:id" element={<AuthWrapper><ComboChiTietPage /></AuthWrapper>} />
                        {/* 17 Kiến thức sản phẩm — ô vẫn "Đang triển khai", vào trang phải gõ mật khẩu admin đặt (kienthuc/KienThucSanPhamPage.js) */}
                        <Route path="/kien-thuc-san-pham" element={<AuthWrapper><KienThucSanPhamPage /></AuthWrapper>} />
                        {/* 23.1 Feedback cá nhân + 24 Kho feedback công ty (đã duyệt) — feedback/feedback-page.js */}
                        <Route path="/feedback-ca-nhan" element={<AuthWrapper><FeedbackDanhSachPage /></AuthWrapper>} />
                        <Route path="/feedback-ca-nhan/:id" element={<AuthWrapper><FeedbackChiTietPage /></AuthWrapper>} />
                        <Route path="/kho-feedback" element={<AuthWrapper><KhoFeedbackPage /></AuthWrapper>} />
                        <Route path="/vinh-danh" element={<AuthWrapper><DangTrienKhaiPage title="Vinh danh" icon="trophy" /></AuthWrapper>} />

                        {/* Reels hàng ngày */}
                        <Route path="/reels-auto-check" element={<AuthWrapper><ReelsAutoCheckList /></AuthWrapper>} />

                        {/* Khoe thành tích (STT 15) — hardcoded, no feature gate */}
                        <Route path="/khoe-thanh-tich" element={<AuthWrapper><KhoeThanhTichPage /></AuthWrapper>} />

                        {/* Khoe thành tích tháng (STT 17) — hardcoded, no feature gate */}
                        <Route path="/khoe-thanh-tich-thang" element={<AuthWrapper><KhoeThanhTichThangPage /></AuthWrapper>} />

                        {/* Thư viện thông điệp (STT 16) — hardcoded, no feature gate */}
                        <Route path="/thu-vien-thong-diep" element={<AuthWrapper><ThuVienThongDiepPage /></AuthWrapper>} />

                        {/* Kho ảnh (STT 18) — gom ảnh mọi chiến dịch, hardcoded, no feature gate */}
                        <Route path="/kho-anh" element={<AuthWrapper><KhoAnhPage /></AuthWrapper>} />

                        {/* Khen thưởng (STT 20) — thẻ chiến dịch + form Đăng ký Du lịch 2026, hardcoded, no feature gate.
                            Route cố định du-lich-2026/... đặt TRƯỚC :ma để không bị nuốt. */}
                        <Route path="/khenthuong" element={<AuthWrapper><KhenThuongPage /></AuthWrapper>} />
                        <Route path="/khenthuong/du-lich-2026/dang-ky" element={<AuthWrapper><DuLich2026Form /></AuthWrapper>} />
                        <Route path="/khenthuong/du-lich-2026/tinh-trang" element={<AuthWrapper><DuLich2026TrangThai /></AuthWrapper>} />
                        <Route path="/khenthuong/du-lich-2026/chuc-mung" element={<AuthWrapper><TheChucMungPage /></AuthWrapper>} />
                        <Route path="/khenthuong/:ma" element={<AuthWrapper><KhenThuongChiTietPage /></AuthWrapper>} />

                        {/* Fallback route - chuyển hướng về /dashboard */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </div>
            </Router>
        </FeatureConfigProvider>
    );
}

export default App;

